import {
  LOGIN_TYPE_WEBLOGIN,
  MATCH_FILTER_CONDITION_ALL,
  STORE_KEY_LOGIN_TYPE
} from 'Constants/index'
import getIntl from 'Helpers/intl'
import { logAppError } from 'Helpers/logger'
import { storeGet, STORE_KEY_ID_LIST } from 'mailup-react-helpers'
import { PREVIEW, SAVE_EXIT } from 'Pages/Campaign/Components/step2/const'
import {
  createMessage,
  deleteConcurrentAccess,
  generatePreview,
  getBeePluginConfig,
  getBeeToken,
  getConcurrentAccessUser,
  getFilters,
  getGroups,
  getListActivation,
  getMessageActivation,
  getMessageDynamicContents,
  getMessageMinimumDetails,
  getPreviewReferenceRecipient,
  getThirdPartyAccepted,
  saveLocalTemplate,
  searchRecipients,
  setThirdPartyResourceAlert,
  updateAutoSave,
  updateBeeMessage,
  updatePreviewReferenceRecipient
} from '../actions/actionEditBee'

import { beePluginConfigState, beePluginState, editorState } from '../state/bee'
import { campaignState } from '../state/campaign'
import { modalConcurrentAccess, modalThirdPartyState } from '../state/modals'
import { pageState } from '../state/page'
import {
  filtersState,
  groupsState,
  previewLastSelected,
  previewState
} from '../state/preview'
import { saveCampaign } from './callbacks'

export const EMAIL_OPTOUT_TYPE_ALL = -1
export const SMS_OPTOUT_TYPE_ALL = -1

export const checkBeePluginCallback =
  ({ set, snapshot }) =>
  async () => {
    set(beePluginState, (prev) => ({
      ...prev,
      loaded: false,
      loading: true
    }))

    const { EmailOptions } = await snapshot.getPromise(campaignState)

    const thirdPartyAccepted = await getThirdPartyAccepted()
    const concurrentAccessUsername = await getConcurrentAccessUser(
      EmailOptions.IdMessage
    )

    try {
      set(editorState, (prev) => ({
        ...prev,
        concurrentAccessUsername,
        thirdPartyAccepted
      }))

      if (!thirdPartyAccepted) {
        set(modalThirdPartyState, (prev) => ({
          ...prev,
          show: true
        }))
        return false
      }

      if (concurrentAccessUsername) {
        set(modalConcurrentAccess, (prev) => ({
          ...prev,
          show: true
        }))
        return false
      }

      set(pageState, (prev) => ({
        ...prev,
        initBeePlugin: thirdPartyAccepted && !concurrentAccessUsername
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'CampaignWizard',
        Method: 'checkBeePluginCallback',
        Message: 'Errore in checkBeePluginCallback'
      })
      set(pageState, {
        error: getIntl().formatMessage({
          id: 'Load.Bee.EditorInitializationError'
        }),
        severity: 'danger'
      })
    }

    set(beePluginState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))
  }

export const initBeePluginCallback =
  ({ set, snapshot }) =>
  async ({ settings }) => {
    set(beePluginState, (prev) => ({
      ...prev,
      loaded: false,
      loading: true
    }))

    // on load
    const onLoad = () => {
      set(beePluginState, (prev) => ({ ...prev, loaded: true }))
    }

    // on error
    const onError = (err) => {
      logAppError({
        appError: err,
        Component: 'CampaignWizard',
        Method: 'BeePluginEditor',
        Message: 'Error triggered by Bee Plugin'
      })
    }

    const { initialStructure } = await snapshot.getPromise(editorState)

    try {
      saveLocalTemplate(initialStructure)

      const token = await getBeeToken()
      const config = await getBeePluginConfig()

      const beeConfig = {
        ...config,
        onLoad,
        onError,
        ...settings
      }

      set(beePluginConfigState, (prev) => ({ ...prev, ...config }))
      window.BeePlugin.create(token, beeConfig, (instance) => {
        set(beePluginState, (prev) => ({ ...prev, instance }))
        instance.start(JSON.parse(initialStructure))
        setTimeout(() =>
          set(beePluginState, (prev) => ({
            ...prev,
            loadedInstance: true,
            loading: false
          }))
        )
      })
    } catch (error) {
      const campaign = await snapshot.getPromise(campaignState)
      logAppError({
        appError: error,
        Component: 'CampaignWizard',
        Method: 'initBeePluginCallback',
        Message: 'Errore in recupero dati bee plugin',
        Data: JSON.stringify({
          id: campaign.Id,
          idTemplate: campaign.EmailOptions.IdTemplate
        })
      })
      set(pageState, {
        error: getIntl().formatMessage({
          id: 'Load.Bee.EditorInitializationError'
        }),
        severity: 'danger'
      })
    }

    set(beePluginState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))
  }

export const onBeeSaveCallback =
  ({ set, snapshot }) =>
  async (jsonFile, htmlFile) => {
    try {
      const { onSave } = await snapshot.getPromise(beePluginState)
      const campaign = await snapshot.getPromise(campaignState)
      let idMsg = campaign.EmailOptions.IdMessage
      if (!idMsg) {
        const { IdMessage } = await createMessage(
          'campaign' + campaign.Id,
          jsonFile,
          htmlFile
        )
        idMsg = IdMessage
        set(campaignState, (prev) => ({
          ...prev,
          EmailOptions: {
            ...prev.EmailOptions,
            IdMessage: idMsg
          }
        }))
      } else {
        await updateBeeMessage(idMsg, {
          Content: htmlFile,
          Structure: jsonFile
        })
      }

      const { Subject } = await getMessageMinimumDetails(idMsg)

      await updateAutoSave(idMsg, {
        content: htmlFile,
        structure: jsonFile,
        subject: Subject
      })

      if (onSave === PREVIEW) {
        set(previewState, (prev) => ({ ...prev, callPreview: true }))
      } else if (onSave === SAVE_EXIT) {
        saveCampaign(3, set, snapshot, idMsg)
      }

      set(editorState, (prev) => ({
        ...prev,
        saved: true
      }))
      set(beePluginState, (prev) => ({
        ...prev,
        onSave: undefined
      }))

      set(editorState, (prev) => ({ ...prev, initialStructure: jsonFile }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'CampaignWizard',
        Method: 'onBeeSaveCallback'
      })

      set(editorState, (prev) => ({
        ...prev,
        saved: false
      }))

      set(pageState, {
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric'
        })
      })
    }
  }

export const updateAutosaveCallback =
  ({ snapshot }) =>
  async (json) => {
    // const { idLP } = await snapshot.getPromise(editorState)

    try {
      // if (idLP) {
      //   removeLocalTemplate()
      // } else {
      //   saveLocalTemplate(json)
      // }
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'CampaignWizard',
        Method: 'updateAutosaveCallback',
        Message: 'Errore in autosave dati bee plugin'
      })
    }
  }

export const showPreviewCallback =
  ({ set, reset, snapshot }) =>
  async (id, autoSave) => {
    const idList = storeGet(STORE_KEY_ID_LIST)
    set(previewState, (prev) => ({
      ...prev,
      active: true,
      loading: true,
      autoSave,
      idList
    }))

    try {
      const messageDynamicContents = await getMessageDynamicContents(id)

      const listActivation = await getListActivation()
      if (listActivation.Items && listActivation.Items.length > 0) {
        await initSelectRecipient(set)
      } else {
        const messageActivation = await getMessageActivation(id)

        if (messageActivation.Items && messageActivation.Items.length > 0) {
          await initSelectRecipient(set)
        } else {
          const { Content } = await generatePreview(id, {
            IdRecipient: 0,
            Autosave: !!autoSave,
            DynamicContentIdList: []
          })

          set(previewState, (prev) => ({
            ...prev,
            active: true,
            loading: false,
            html: Content,
            dynamicContents: messageDynamicContents
          }))
        }
      }

      set(previewState, (prev) => ({
        ...prev,
        active: true,
        loading: false,
        dynamicContents: messageDynamicContents
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'MessageEdit',
        Method: 'showPreviewCallback'
      })

      set(previewState, (prev) => ({
        active: true,
        loading: false,
        error: true
      }))
    }
  }

async function initSelectRecipient(set) {
  set(previewState, (prev) => ({
    ...prev,
    hasPersonalization: true,
    showPersonalization: true
  }))

  const groups = await getGroups()
  set(groupsState, groups)

  const filters = await getFilters()
  set(filtersState, filters)

  const idList = storeGet(STORE_KEY_ID_LIST)
  const previewReferenceRecipient = await getPreviewReferenceRecipient(idList)

  let lastFilters = []
  let lastGroups = []
  let lastEmails = []

  if (previewReferenceRecipient) {
    lastGroups = buildLastElementList(
      previewReferenceRecipient.lastGroups,
      [
        ...groups,
        {
          label: getIntl().formatMessage({
            id: 'Load.SearchRecipients.nessunGruppo'
          }),
          value: 0
        }
      ],
      previewReferenceRecipient.lastTypeSelected === 'group'
    )
    lastFilters = buildLastElementList(
      previewReferenceRecipient.lastFilters,
      filters.options,
      previewReferenceRecipient.lastTypeSelected === 'filter'
    )
    lastEmails = buildLastElementList(
      previewReferenceRecipient.lastEmails,
      undefined,
      previewReferenceRecipient.lastTypeSelected === 'email'
    )

    set(previewLastSelected, (prev) => ({
      ...prev,
      lastGroups,
      lastFilters,
      lastEmails,
      previewReferenceRecipient
    }))
  }
}

const buildLastElementList = (list, fullList, selected) => {
  try {
    const result = []
    // eslint-disable-next-line array-callback-return
    list.map((idElement, index) => {
      if (idElement != null) {
        let labels = []
        if (fullList) {
          labels = fullList.filter(
            (listElement) => listElement.value === idElement
          )
        }

        result.push({
          id: idElement,
          value: labels.length > 0 ? labels[0].label : idElement,
          selected: selected && index === 0
        })
      }
    })
    return result
  } catch (error) {
    logAppError({
      appError: error
    })
  }
  return []
}

export const updatePreviewCallback =
  ({ set, snapshot }) =>
  async (withDynamicContents) => {
    const { EmailOptions } = await snapshot.getPromise(campaignState)
    const { IdRecipient, autoSave } = await snapshot.getPromise(previewState)

    set(previewState, (prev) => ({
      ...prev,
      active: true,
      loading: true
    }))

    try {
      const { Content } = await generatePreview(EmailOptions.IdMessage, {
        IdRecipient,
        Autosave: !!autoSave,
        DynamicContentIdList: withDynamicContents
      })

      set(previewState, (prev) => ({
        ...prev,
        active: true,
        loading: false,
        selectedDynamicContents: withDynamicContents,
        html: Content
      }))
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'MessageEdit',
        Method: 'updatePreviewCallback'
      })

      set(previewState, (prev) => ({
        ...prev,
        active: true,
        loading: false,
        error: true
      }))
    }
  }

export const searchRecipientsCallback =
  ({ set, snapshot }) =>
  async (search, preferenceRecipient, dynamicFields) => {
    const { previewReferenceRecipient } =
      await snapshot.getPromise(previewLastSelected)
    set(previewState, (prev) => ({
      ...prev,
      loading: true
    }))

    const defaultSearchState = {
      channel: undefined,
      status: undefined,
      email: '',
      phonePrefix: '',
      phoneNumber: '',
      includeGroups: [],
      excludeGroups: [],
      applyFilters: [],
      applyFiltersOr: MATCH_FILTER_CONDITION_ALL,
      matchFieldsMap: new Map(),
      matchFields: [],
      matchFieldsValues: [],
      subscribedFrom: null,
      subscribedTo: null,
      reqSubscribedFrom: null,
      reqSubscribedTo: null,
      unsubscribedFrom: null,
      unsubscribedTo: null,
      optOutTypeEmail: EMAIL_OPTOUT_TYPE_ALL,
      optOutTypeSms: SMS_OPTOUT_TYPE_ALL,
      optOutMessage: 0,
      orderBy: 'Email',
      orderDescending: true,
      advancedSearchExpanded: false,
      pageSize: 50
    }

    try {
      const results = await searchRecipients(
        { ...defaultSearchState, ...search },
        dynamicFields
      )

      const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
      if (loginType === LOGIN_TYPE_WEBLOGIN && preferenceRecipient) {
        const updateUserResult = await updatePreviewReferenceRecipient({
          ...previewReferenceRecipient,
          ...preferenceRecipient
        })

        set(previewLastSelected, (prev) => ({
          ...prev,
          previewReferenceRecipient: JSON.parse(updateUserResult.DataValue)
        }))
      }

      if (results.TotalItems > 0) {
        const max = results.TotalItems < 50 ? results.TotalItems - 1 : 49
        const randomIndex = Math.floor(Math.random() * (max + 1))
        const idUser = results.Items[randomIndex].id
        const recipient = results.Items[randomIndex].fields.get('Email')
        await updatePreviewFromSelectedRecipient(
          set,
          snapshot,
          idUser,
          recipient
        )
      }

      set(previewState, (prev) => ({
        ...prev,
        loading: false
      }))

      return results
    } catch (error) {
      logAppError({
        appError: error
      })

      set(previewState, (prev) => ({
        ...prev,
        loading: false
      }))
    }
  }

async function updatePreviewFromSelectedRecipient(
  set,
  snapshot,
  IdRecipient,
  recipient
) {
  const { EmailOptions } = await snapshot.getPromise(campaignState)
  const { selectedDynamicContents, autoSave } =
    await snapshot.getPromise(previewState)

  set(previewState, (prev) => ({
    ...prev,
    active: true,
    loading: true
  }))

  try {
    const { Content } = await generatePreview(EmailOptions.IdMessage, {
      IdRecipient,
      Autosave: !!autoSave,
      DynamicContentIdList: selectedDynamicContents
    })

    set(previewState, (prev) => ({
      ...prev,
      active: true,
      loading: false,
      showPersonalization: false,
      html: Content,
      IdRecipient,
      recipient
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'updatePreviewCallback'
    })

    set(previewState, (prev) => ({
      ...prev,
      active: true,
      loading: false,
      error: true
    }))
  }
}

export const updateThirdPartyCallback =
  ({ set, snapshot, reset }) =>
  async () => {
    set(editorState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
      if (loginType === LOGIN_TYPE_WEBLOGIN) {
        await setThirdPartyResourceAlert()
      }
      set(editorState, (prev) => ({
        ...prev,
        thirdPartyAccepted: true
      }))
      set(modalThirdPartyState, (prev) => ({ ...prev, show: false }))
    } catch (error) {
      logAppError({
        appError: error
      })
      set(pageState, {
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      })
    }
    set(editorState, (prev) => ({
      ...prev,
      loading: false
    }))

    const { concurrentAccessUsername } = await snapshot.getPromise(editorState)
    if (concurrentAccessUsername) {
      set(modalConcurrentAccess, (prev) => ({
        ...prev,
        show: true
      }))
    } else {
      set(pageState, (prev) => ({
        ...prev,
        initBeePlugin: true
      }))
    }
  }

export const exitCallback =
  ({ set, snapshot, reset }) =>
  async (
    check = false,
    onConfirmRedirect,
    comesFromModalConcurrentAccess = false
  ) => {
    const { messageType, dirty, saved } = await snapshot.getPromise(editorState)
    const { EmailOptions } = await snapshot.getPromise(campaignState)

    if (comesFromModalConcurrentAccess || (!dirty && saved)) {
      await deleteConcurrentAccess(EmailOptions.IdMessage)
      // await deleteAutosave(id) non abbiamo autosave

      onConfirmRedirect(messageType)
    }
  }
